import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, Eye as EyeIcon, UserCircle2 as ContactsLogoIcon } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InputAdornment from '@mui/material/InputAdornment';
import Badge from '@mui/material/Badge';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';

import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import PrintIcon from '@mui/icons-material/Print';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useJob } from '../context/JobContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatWidget = ({ title, value, icon: Icon, color, active, onClick }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      borderColor: active ? color : undefined,
      bgcolor: active ? `${color}08` : 'background.paper',
      transition: 'border-color 0.2s ease, background-color 0.2s ease',
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color: color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const validationSchema = Yup.object({
  type: Yup.string().required('Tipo richiesto'),
  status: Yup.string().required('Stato richiesto'),
  email: Yup.string().email('Email non valida'),
  phone: Yup.string(),
  address: Yup.string(),
  city: Yup.object().nullable(),
  
  // Conditional validation based on type
  first_name: Yup.string().when('type', {
    is: 'individual',
    then: (schema) => schema.required('Nome richiesto'),
  }),
  last_name: Yup.string().when('type', {
    is: 'individual',
    then: (schema) => schema.required('Cognome richiesto'),
  }),
  company_name: Yup.string().when('type', {
    is: 'company',
    then: (schema) => schema.required('Ragione Sociale richiesta'),
  }),
});

const CONTACT_STATUS_LABELS = {
  lead: 'Lead',
  prospect: 'Prospect',
  client: 'Cliente',
  churned: 'Perso'
};

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('desc');
  
  const [search, setSearch] = useState('');
  
  // Filter States
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState(null); // Selected city object for filtering
  const [filterCityOptions, setFilterCityOptions] = useState([]);
  const [loadingFilterCity, setLoadingFilterCity] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [contactType, setContactType] = useState('individual');
  const [selected, setSelected] = useState([]);
  const { addJob } = useJob();
  const [stats, setStats] = useState({ lead: 0, prospect: 0, client: 0, churned: 0 });

  // Delete Confirmation Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleteDependencies, setDeleteDependencies] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // City Autocomplete State (for Form)
  const [cityOpen, setCityOpen] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingCity, setLoadingCity] = useState(false);
  
  const handleCitySearch = async (event, value, isFilter = false) => {
    // If value is null/undefined, use empty string for default search
    const query = value || '';
    
    if (isFilter) setLoadingFilterCity(true);
    else setLoadingCity(true);

    try {
      const res = await api.get('/cities/search', { params: { q: query } });
      if (isFilter) setFilterCityOptions(res.data);
      else setCityOptions(res.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      if (isFilter) setLoadingFilterCity(false);
      else setLoadingCity(false);
    }
  };

  const fetchContacts = React.useCallback(async () => {
    try {
      const params = {
        search,
        type: filterType !== 'all' ? filterType : undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        city_id: filterCity ? filterCity.id : undefined,
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection,
        _t: new Date().getTime() // Prevent caching
      };
      
      console.log('Fetching contacts with params:', params);
      const res = await api.get('/contacts', { params });
      console.log('Contacts response:', res.data);

      // Handle both new paginated response and potential legacy response format
      if (res.data && Array.isArray(res.data.data)) {
        setContacts(res.data.data);
        setTotal(res.data.total);
      } else if (Array.isArray(res.data)) {
        setContacts(res.data);
        setTotal(res.data.length);
      } else {
        console.error('Invalid contacts data format:', res.data);
        setContacts([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Errore nel caricamento dei contatti');
    }
  }, [search, filterType, filterStatus, filterCity, page, rowsPerPage, orderBy, orderDirection]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/contacts/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [fetchContacts]);

  const handleWidgetClick = (status) => {
    // If clicking the same status, clear the filter (toggle behavior)
    if (filterStatus === status) {
      setFilterStatus('all');
    } else {
      setFilterStatus(status);
    }
    setPage(0); // Reset to first page
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = contacts.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handlePrintPdf = async () => {
    try {
      const startRes = await api.post('/contacts/report', { ids: selected });
      const jobId = startRes.data.jobId;
      addJob(jobId, 'contact');
      setSelected([]);
    } catch (error) {
      console.error('Error starting report job:', error);
      toast.error('Errore nell\'avvio della generazione del PDF');
    }
  };

  const handleOpen = (contact = null) => {
    if (contact) {
      setCurrentId(contact.id);
      setContactType(contact.type);
      formik.setValues({
        type: contact.type,
        status: contact.status,
        email: contact.email || '',
        phone: contact.phone || '',
        address: contact.address || '',
        city_id: contact.city_id || null,
        city: contact.city_name ? { 
          id: contact.city_id, 
          name: contact.city_name, 
          postal_code: contact.postal_code, 
          province: contact.province 
        } : null,
        notes: contact.notes || '',
        
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        fiscal_code: contact.individual_fiscal_code || contact.company_fiscal_code || '',
        birth_date: contact.birth_date ? contact.birth_date.split('T')[0] : '',
        birth_place: contact.birth_place || '',
        gender: contact.gender || 'M',
        
        company_name: contact.company_name || '',
        vat_number: contact.vat_number || '',
        sdi_code: contact.sdi_code || '',
        pec: contact.pec || '',
      });
    } else {
      setCurrentId(null);
      setContactType('individual');
      formik.resetForm();
      formik.setFieldValue('type', 'individual');
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentId(null);
    formik.resetForm();
  };

  const handleDeleteClick = async (contact) => {
    setContactToDelete(contact);
    setDeleting(true);
    setDeleteDependencies(null);
    setDeleteDialogOpen(true);

    try {
      // Try to delete to check dependencies (backend will return 409 if dependencies exist)
      // Actually, better to have a dedicated endpoint or handle the 409 error
      // Let's use the delete endpoint but catch the 409
      // Or we can just try to delete and if it fails with 409, show the dependencies
      // But we want to show "if you confirm..." BEFORE deleting.
      // So we need a way to check dependencies first.
      // Since we implemented the check inside delete controller, we can try to delete
      // and if we get 409, we show the confirmation dialog with force option.
      
      // Wait, the requirement is "se confermi verrà eliminato : (elenco di cosa elmini ..)"
      // So we should probably check dependencies first.
      // Since we don't have a dedicated "check-dependencies" endpoint, we can use the 409 response
      // from the delete attempt as the "check".
      
      // Let's modify the flow:
      // 1. User clicks delete
      // 2. We try to delete
      // 3. If success -> done
      // 4. If 409 -> Show dialog with dependencies -> User confirms -> Call delete with force=true
      
      await api.delete(`/contacts/${contact.id}`);
      toast.success('Contatto eliminato con successo');
      fetchContacts();
      setDeleteDialogOpen(false);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        // Dependencies found
        setDeleteDependencies(error.response.data.dependencies);
        // Keep dialog open to show dependencies
      } else {
        console.error(error);
        toast.error('Errore nell\'eliminazione del contatto');
        setDeleteDialogOpen(false);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/contacts/${contactToDelete.id}?force=true`);
      toast.success('Contatto e dati collegati eliminati con successo');
      fetchContacts();
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      setDeleteDependencies(null);
    } catch (error) {
      console.error(error);
      toast.error('Errore durante l\'eliminazione forzata');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setContactToDelete(null);
    setDeleteDependencies(null);
  };

  const formik = useFormik({
    initialValues: {
      type: 'individual',
      status: 'lead',
      email: '',
      phone: '',
      address: '',
      city_id: null,
      city: null,
      notes: '',
      
      first_name: '',
      last_name: '',
      fiscal_code: '',
      birth_date: '',
      birth_place: '',
      gender: 'M',
      
      company_name: '',
      vat_number: '',
      sdi_code: '',
      pec: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      console.log('Form submitted with values:', values);
      try {
        // Ensure type matches the toggle
        values.type = contactType;
        
        if (currentId) {
          console.log('Updating contact:', currentId);
          await api.put(`/contacts/${currentId}`, values);
          toast.success('Contatto aggiornato con successo');
        } else {
          console.log('Creating new contact');
          await api.post('/contacts', values);
          toast.success('Contatto creato con successo');
        }
        handleClose();
        fetchContacts();
      } catch (error) {
        console.error('Submit error:', error);
        toast.error('Errore nel salvataggio del contatto');
      }
    },
  });

  // Debug form errors
  useEffect(() => {
    if (formik.submitCount > 0 && !formik.isValid) {
      console.error('Form validation errors:', formik.errors);
      toast.error('Per favore correggi gli errori nel modulo');
    }
  }, [formik.submitCount, formik.isValid, formik.errors]);

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setContactType(newType);
      formik.setFieldValue('type', newType);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'lead': return 'info';
      case 'prospect': return 'warning';
      case 'client': return 'success';
      case 'churned': return 'error';
      default: return 'default';
    }
  };

  const activeFiltersCount = [
    filterType !== 'all',
    filterStatus !== 'all',
    filterCity !== null
  ].filter(Boolean).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ContactsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Gestione Contatti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anagrafica contatti e stato commerciale (Lead, Prospect, Cliente)
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintPdf}
            disabled={selected.length === 0}
            sx={{
              borderRadius: '10px !important',
              textTransform: 'none',
              px: 2.5,
              height: 40,
              minWidth: 124,
              borderColor: '#94a3b8',
              color: '#334155',
              bgcolor: 'transparent',
              '&:hover': {
                borderColor: '#64748b',
                bgcolor: '#f8fafc'
              },
              '&.Mui-disabled': {
                borderColor: '#cbd5e1',
                color: '#9ca3af',
                bgcolor: 'transparent'
              }
            }}
          >
            Stampa PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Lead"
            value={stats.lead}
            icon={PersonAddIcon}
            color="#0ea5e9"
            active={filterStatus === 'lead'}
            onClick={() => handleWidgetClick('lead')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Prospect"
            value={stats.prospect}
            icon={TrendingUpIcon}
            color="#f59e0b"
            active={filterStatus === 'prospect'}
            onClick={() => handleWidgetClick('prospect')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Cliente"
            value={stats.client}
            icon={CheckCircleIcon}
            color="#22c55e"
            active={filterStatus === 'client'}
            onClick={() => handleWidgetClick('client')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Perso"
            value={stats.churned}
            icon={CancelIcon}
            color="#ef4444"
            active={filterStatus === 'churned'}
            onClick={() => handleWidgetClick('churned')}
          />
        </Grid>
      </Grid>

      <StyledCard sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Cerca contatti..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton onClick={() => setShowFilters(!showFilters)}>
                    <Badge badgeContent={activeFiltersCount} color="primary">
                      <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                    </Badge>
                  </IconButton>
                  <IconButton>
                    <ViewColumnIcon color="action" />
                  </IconButton>
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 500 }}
        />
      </StyledCard>

      {showFilters && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-type-label">Filtra per Tipo</InputLabel>
              <Select
                labelId="filter-type-label"
                id="filter-type"
                value={filterType}
                label="Filtra per Tipo"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="individual">Persona Fisica</MenuItem>
                <MenuItem value="company">Azienda</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-status-label">Filtra per Stato</InputLabel>
              <Select
                labelId="filter-status-label"
                id="filter-status"
                value={filterStatus}
                label="Filtra per Stato"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="lead">Lead</MenuItem>
                <MenuItem value="prospect">Prospect</MenuItem>
                <MenuItem value="client">Cliente</MenuItem>
                <MenuItem value="churned">Perso</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              id="filter-city"
              options={filterCityOptions}
              getOptionLabel={(option) => {
                const country = option.country || '';
                const province = (option.province && option.province !== 'null' && option.province !== 'NULL') ? ` (${option.province})` : '';
                return option.name ? `${option.name}${province} - ${country}` : '';
              }}
              filterOptions={(x) => x}
              value={filterCity}
              onChange={(event, newValue) => {
                setFilterCity(newValue);
              }}
              onOpen={() => {
                if (filterCityOptions.length === 0) {
                  handleCitySearch(null, '', true);
                }
              }}
              onInputChange={(event, newInputValue) => {
                handleCitySearch(event, newInputValue, true);
              }}
              loading={loadingFilterCity}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filtra per Città"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingFilterCity ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
      )}

      <StyledCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < contacts.length}
                    checked={contacts.length > 0 && selected.length === contacts.length}
                    onChange={handleSelectAllClick}
                    inputProps={{
                      'aria-label': 'select all contacts',
                    }}
                  />
                </TableCell>
                <TableCell sortDirection={orderBy === 'name' ? orderDirection : false}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome / Ragione Sociale
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'type' ? orderDirection : false}>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'status' ? orderDirection : false}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'email' ? orderDirection : false}>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Contatti
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'city' ? orderDirection : false}>
                  <TableSortLabel
                    active={orderBy === 'city'}
                    direction={orderBy === 'city' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('city')}
                  >
                    Città
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => {
                const isSelected = selected.indexOf(contact.id) !== -1;
                return (
                  <TableRow 
                    key={contact.id} 
                    hover
                    role="checkbox"
                    aria-checked={isSelected}
                    selected={isSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isSelected}
                        onChange={(event) => handleClick(event, contact.id)}
                        inputProps={{
                          'aria-labelledby': `enhanced-table-checkbox-${contact.id}`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                    {contact.type === 'individual' 
                      ? `${contact.first_name} ${contact.last_name}`
                      : contact.company_name}
                  </TableCell>
                  <TableCell>
                    {contact.type === 'individual' 
                      ? <Chip icon={<PersonIcon />} label="Privato" size="small" variant="outlined" />
                      : <Chip icon={<BusinessIcon />} label="Azienda" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={CONTACT_STATUS_LABELS[contact.status] || contact.status} 
                      color={getStatusColor(contact.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="caption">{contact.email}</Typography>
                      <Typography variant="caption">{contact.phone}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {contact.city_name}
                    {contact.city_name && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {contact.province} {contact.country && contact.country !== 'Italia' ? `, ${contact.country}` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => navigate(`/contacts/${contact.id}`)} color="info" size="small">
                      <EyeIcon size={18} />
                    </IconButton>
                    <IconButton onClick={() => handleOpen(contact)} color="primary" size="small">
                      <EditIcon size={18} />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(contact)} color="error" size="small">
                      <DeleteIcon size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {contacts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun contatto trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`}
        />
      </StyledCard>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Conferma eliminazione contatto"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {deleteDependencies ? (
              <>
                <Typography variant="body1" gutterBottom color="error" fontWeight="bold">
                  Attenzione: questo contatto ha dei dati collegati.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Se confermi, verranno eliminati anche:
                </Typography>
                <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                  {deleteDependencies.quotes > 0 && (
                    <li><strong>{deleteDependencies.quotes}</strong> Preventivi</li>
                  )}
                  {deleteDependencies.debt_positions > 0 && (
                    <li><strong>{deleteDependencies.debt_positions}</strong> Posizioni Debitorie</li>
                  )}
                  {deleteDependencies.contracts > 0 && (
                    <li><strong>{deleteDependencies.contracts}</strong> Contratti</li>
                  )}
                </ul>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Questa azione è irreversibile.
                </Alert>
              </>
            ) : (
              "Sei sicuro di voler eliminare questo contatto?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Annulla
          </Button>
          <Button onClick={deleteDependencies ? handleConfirmDelete : handleConfirmDelete} color="error" autoFocus disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-contact-title"
      >
        <Box sx={style}>
          <Typography id="modal-contact-title" variant="h6" component="h2" mb={3}>
            {currentId ? 'Modifica Contatto' : 'Nuovo Contatto'}
          </Typography>
          
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  color="primary"
                  value={contactType}
                  exclusive
                  onChange={handleTypeChange}
                  fullWidth
                  disabled={!!currentId} // Disable type change on edit to avoid complexity
                >
                  <ToggleButton value="individual">Persona Fisica</ToggleButton>
                  <ToggleButton value="company">Azienda (Giuridica)</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="status-label">Stato</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formik.values.status}
                    label="Stato"
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="lead">Lead</MenuItem>
                    <MenuItem value="prospect">Prospect</MenuItem>
                    <MenuItem value="client">Cliente</MenuItem>
                    <MenuItem value="churned">Perso</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Fields for Individual */}
            {contactType === 'individual' && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>Dati Personali</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="first_name"
                      name="first_name"
                      label="Nome"
                      value={formik.values.first_name}
                      onChange={formik.handleChange}
                      error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                      helperText={formik.touched.first_name && formik.errors.first_name}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="last_name"
                      name="last_name"
                      label="Cognome"
                      value={formik.values.last_name}
                      onChange={formik.handleChange}
                      error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                      helperText={formik.touched.last_name && formik.errors.last_name}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="fiscal_code"
                      name="fiscal_code"
                      label="Codice Fiscale"
                      value={formik.values.fiscal_code}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel id="gender-label">Sesso</InputLabel>
                      <Select
                        labelId="gender-label"
                        id="gender"
                        name="gender"
                        value={formik.values.gender}
                        label="Sesso"
                        onChange={formik.handleChange}
                      >
                        <MenuItem value="M">Maschio</MenuItem>
                        <MenuItem value="F">Femmina</MenuItem>
                        <MenuItem value="Other">Altro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="birth_date"
                      name="birth_date"
                      label="Data di Nascita"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.birth_date}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="birth_place"
                      name="birth_place"
                      label="Luogo di Nascita"
                      value={formik.values.birth_place}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Fields for Company */}
            {contactType === 'company' && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>Dati Aziendali</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="company_name"
                      name="company_name"
                      label="Ragione Sociale"
                      value={formik.values.company_name}
                      onChange={formik.handleChange}
                      error={formik.touched.company_name && Boolean(formik.errors.company_name)}
                      helperText={formik.touched.company_name && formik.errors.company_name}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="vat_number"
                      name="vat_number"
                      label="Partita IVA"
                      value={formik.values.vat_number}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="fiscal_code"
                      name="fiscal_code"
                      label="Codice Fiscale"
                      value={formik.values.fiscal_code}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="sdi_code"
                      name="sdi_code"
                      label="Codice SDI"
                      value={formik.values.sdi_code}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      id="pec"
                      name="pec"
                      label="PEC"
                      value={formik.values.pec}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>Contatti e Indirizzo</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="phone"
                    name="phone"
                    label="Telefono"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="address"
                    name="address"
                    label="Indirizzo"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    id="city-autocomplete"
                    open={cityOpen}
                    onOpen={() => {
                      setCityOpen(true);
                      if (cityOptions.length === 0) {
                        handleCitySearch(null, '', false);
                      }
                    }}
                    onClose={() => setCityOpen(false)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => {
                      const country = option.country || '';
                      const province = (option.province && option.province !== 'null' && option.province !== 'NULL') ? ` (${option.province})` : '';
                      return option.name ? `${option.name}${province} - ${country}` : '';
                    }}
                    options={cityOptions}
                    loading={loadingCity}
                    onInputChange={(event, newInputValue) => {
                      handleCitySearch(event, newInputValue, false);
                    }}
                    value={formik.values.city}
                    onChange={(event, newValue) => {
                      formik.setFieldValue('city', newValue);
                      if (newValue) {
                        formik.setFieldValue('city_id', newValue.id);
                        formik.setFieldValue('postal_code', newValue.postal_code);
                        formik.setFieldValue('province', newValue.province);
                      } else {
                        formik.setFieldValue('city_id', null);
                        formik.setFieldValue('postal_code', '');
                        formik.setFieldValue('province', '');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Città"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loadingCity ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    id="postal_code"
                    name="postal_code"
                    label="CAP"
                    value={formik.values.postal_code}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    id="province"
                    name="province"
                    label="Provincia"
                    value={formik.values.province}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <TextField
              fullWidth
              id="notes"
              name="notes"
              label="Note"
              multiline
              rows={3}
              value={formik.values.notes}
              onChange={formik.handleChange}
              margin="normal"
            />

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleClose} color="inherit">
                Annulla
              </Button>
              <Button type="submit" variant="contained">
                Salva
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </Box>
  );
}
